export type SuffixFormatter = (n: number) => string;

/**
 * Parse trailing number suffix from a name. Recognizes:
 * - "Polc 1" (space + digits)
 * - "Polc1" (no separator)
 * - "Polc (1)" or "Polc(1)" (parentheses)
 * - "Polc [1]" or "Polc[1]" (square brackets)
 * - "Polc {1}" or "Polc{1}" (curly braces)
 * - "Polc <1>" or "Polc<1>" (angle brackets)
 *
 * Returns { base, num, formatter } where:
 * - base = the name without the numeric suffix ("Szekrényem 1" → "Szekrényem")
 * - num = the parsed number (or null if none)
 * - formatter = function that builds a new name with a given number,
 *               preserving the original delimiter style
 */
export const parseNameSuffix = (
    name: string
): { base: string; num: number | null; formatter: SuffixFormatter } => {
    const trimmed = name.trim();

    const wrappers: Array<[string, string, RegExp]> = [
        ["(", ")", /^(.*?)\s*\((\d+)\)$/],
        ["[", "]", /^(.*?)\s*\[(\d+)\]$/],
        ["{", "}", /^(.*?)\s*\{(\d+)\}$/],
        ["<", ">", /^(.*?)\s*<(\d+)>$/],
    ];
    for (const [open, close, re] of wrappers) {
        const m = trimmed.match(re);
        if (m) {
            const base = m[1].trim();
            return {
                base,
                num: parseInt(m[2], 10),
                formatter: (n) => `${base} ${open}${n}${close}`,
            };
        }
    }

    const plain = trimmed.match(/^(.*?)\s*(\d+)$/);
    if (plain) {
        const base = plain[1].trim();
        return {
            base,
            num: parseInt(plain[2], 10),
            formatter: (n) => `${base} ${n}`,
        };
    }

    return {
        base: trimmed,
        num: null,
        formatter: (n) => `${trimmed} ${n}`,
    };
};

/**
 * The name a ucon + StyleScript pair gives its root: the last StyleScript entry that sets Name on
 * the root (no TargetType; Path "" or the ucon's root key, e.g. {"Name": "Konyha", "Path": "Room"}),
 * else the Name of the ucon's root. Null when neither names it.
 * Elements named inside it ({"Path": "*.Korpusz.Left", "Name": ...}, a TargetType, a Name nested
 * in AddChild / Children, a Name below the ucon's root) do not name the design.
 */
export const scriptDesignName = (ucon: unknown, style: unknown): string | null => {
    const rootKey = ucon && typeof ucon === "object" ? Object.keys(ucon)[0] : undefined;
    if (Array.isArray(style)) {
        for (let i = style.length - 1; i >= 0; i--) {
            const entry = style[i];
            if (
                typeof entry?.Name === "string" && entry.Name.trim() !== "" && entry.TargetType == null &&
                (entry.Path === "" || entry.Path === rootKey)
            ) {
                return entry.Name.trim();
            }
        }
    }
    const rootName = rootKey ? (ucon as Record<string, any>)[rootKey]?.Name : undefined;
    return typeof rootName === "string" && rootName.trim() !== "" ? rootName.trim() : null;
};
